import React, { useState, useRef } from 'react';
import { ChevronRight, ChevronLeft, Download, Upload, FileText, Lightbulb, Settings, Eye, CheckCircle } from 'lucide-react';

const ProvisionalPatentChatbot = () => {
  const [currentSection, setCurrentSection] = useState(0);
  const [responses, setResponses] = useState({});
  const [uploadedImages, setUploadedImages] = useState([]);
  const fileInputRef = useRef(null);

  const sections = [
    {
      id: 'intro',
      title: 'Welcome to Your Provisional Patent Assistant',
      icon: <Lightbulb className="w-6 h-6" />
    },
    {
      id: 'background',
      title: 'Section 1: Background',
      icon: <FileText className="w-6 h-6" />
    },
    {
      id: 'summary',
      title: 'Section 2: Summary of the Invention',
      icon: <Settings className="w-6 h-6" />
    },
    {
      id: 'drawings',
      title: 'Section 3: Brief Description of Drawings',
      icon: <Eye className="w-6 h-6" />
    },
    {
      id: 'detailed',
      title: 'Section 4: Detailed Description',
      icon: <FileText className="w-6 h-6" />
    },
    {
      id: 'review',
      title: 'Section 5: Review & Page Count Check',
      icon: <CheckCircle className="w-6 h-6" />
    }
  ];

  const updateResponse = (key, value) => {
    setResponses(prev => ({ ...prev, [key]: value }));
  };

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedImages(prev => [...prev, {
          id: Date.now() + Math.random(),
          name: file.name,
          data: e.target.result
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const generateDocument = () => {
    const content = `
PROVISIONAL PATENT APPLICATION

TITLE OF INVENTION: ${responses.inventionTitle || '[Please provide invention title]'}

BACKGROUND OF THE INVENTION

Technical Field:
${responses.technicalField || '[Technical field not provided]'}

Prior Art:
${responses.priorArt || '[Prior art not provided]'}

Technical Problem:
${responses.technicalProblem || '[Technical problem not provided]'}

Problem Significance:
${responses.problemSignificance || '[Problem significance not provided]'}

Previous Solutions:
${responses.previousSolutions || '[Previous solutions not provided]'}

SUMMARY OF THE INVENTION

High-Level Summary:
${responses.inventionSummary || '[Invention summary not provided]'}

Core Inventive Elements:
${responses.coreElements || '[Core elements not provided]'}

Advantages:
${responses.advantages || '[Advantages not provided]'}

Beneficiaries:
${responses.beneficiaries || '[Beneficiaries not provided]'}

BRIEF DESCRIPTION OF THE DRAWINGS

${responses.drawingDescriptions || '[Drawing descriptions not provided]'}

DETAILED DESCRIPTION OF THE INVENTION

${responses.detailedDescription || '[Detailed description not provided]'}

Components and Operations:
${responses.componentDetails || '[Component details not provided]'}

Alternative Embodiments:
${responses.alternatives || '[Alternative embodiments not provided]'}

Manufacturing and Assembly:
${responses.manufacturing || '[Manufacturing details not provided]'}

FIGURES AND DRAWINGS
${uploadedImages.length > 0 ? `${uploadedImages.length} figures uploaded` : 'No figures uploaded'}
    `;

    const blob = new Blob([content], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'provisional_patent_application.doc';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const estimateWordCount = () => {
    const allText = Object.values(responses).join(' ');
    return allText.split(' ').filter(word => word.length > 0).length;
  };

  const renderIntroSection = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Welcome to Your Provisional Patent Assistant!</h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          I'm here to help you gather all essential information to write a strong provisional patent application. 
          The more detailed and thorough your answers—including technical challenges, drawings, and descriptions—the 
          stronger your protection will be.
        </p>
        <div className="bg-white p-4 rounded-md border-l-4 border-blue-500">
          <p className="text-sm text-gray-600">
            <strong>Important:</strong> This tool helps you prepare your application content. You'll still need to 
            complete the official USPTO Form SB/16 and submit your application through the proper channels.
          </p>
        </div>
      </div>
      
      <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700">
          Let's start with the title of your invention:
        </label>
        <input
          type="text"
          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Enter a descriptive title for your invention"
          value={responses.inventionTitle || ''}
          onChange={(e) => updateResponse('inventionTitle', e.target.value)}
        />
      </div>
    </div>
  );

  const renderBackgroundSection = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-800">Section 1: Background of the Invention</h2>
      
      <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700">
          What is the technical field of your invention? (e.g., "medical devices," "network security," "consumer electronics")
        </label>
        <textarea
          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows="3"
          placeholder="Describe the technical field..."
          value={responses.technicalField || ''}
          onChange={(e) => updateResponse('technicalField', e.target.value)}
        />
      </div>

      <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700">
          What was the existing technology or state of the art before your invention?
        </label>
        <textarea
          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows="4"
          placeholder="Describe the prior art and existing solutions..."
          value={responses.priorArt || ''}
          onChange={(e) => updateResponse('priorArt', e.target.value)}
        />
      </div>

      <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700">
          What specific technical problem or deficiency does the existing technology fail to address?
        </label>
        <textarea
          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows="4"
          placeholder="Identify the specific technical problem..."
          value={responses.technicalProblem || ''}
          onChange={(e) => updateResponse('technicalProblem', e.target.value)}
        />
      </div>

      <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700">
          Why is this problem significant? Who is affected by this problem (industry, users, etc.)?
        </label>
        <textarea
          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows="4"
          placeholder="Explain the significance and impact of the problem..."
          value={responses.problemSignificance || ''}
          onChange={(e) => updateResponse('problemSignificance', e.target.value)}
        />
      </div>

      <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700">
          Has anyone tried to solve this problem before? If so, how do their solutions fall short?
        </label>
        <textarea
          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows="4"
          placeholder="Describe previous attempts and their shortcomings..."
          value={responses.previousSolutions || ''}
          onChange={(e) => updateResponse('previousSolutions', e.target.value)}
        />
      </div>
    </div>
  );

  const renderSummarySection = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-800">Section 2: Summary of the Invention</h2>
      
      <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700">
          Provide a high-level summary of the invention. What is the technical solution to the identified problem?
        </label>
        <textarea
          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows="5"
          placeholder="Describe your technical solution..."
          value={responses.inventionSummary || ''}
          onChange={(e) => updateResponse('inventionSummary', e.target.value)}
        />
      </div>

      <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700">
          What are the core inventive elements or features?
        </label>
        <textarea
          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows="4"
          placeholder="List and describe the key inventive features..."
          value={responses.coreElements || ''}
          onChange={(e) => updateResponse('coreElements', e.target.value)}
        />
      </div>

      <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700">
          What advantages does your invention provide compared to the prior art?
        </label>
        <textarea
          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows="4"
          placeholder="Explain the advantages and improvements..."
          value={responses.advantages || ''}
          onChange={(e) => updateResponse('advantages', e.target.value)}
        />
      </div>

      <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700">
          To whom is this invention most beneficial? (users, manufacturers, etc.)
        </label>
        <textarea
          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows="3"
          placeholder="Identify the primary beneficiaries..."
          value={responses.beneficiaries || ''}
          onChange={(e) => updateResponse('beneficiaries', e.target.value)}
        />
      </div>
    </div>
  );

  const renderDrawingsSection = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-800">Section 3: Brief Description of Drawings</h2>
      
      <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
        <p className="text-sm text-yellow-800">
          <strong>Important:</strong> Please supply as many technical drawings, diagrams, flowcharts, block diagrams, 
          photographs, or sketches as possible that depict various views or aspects of your invention.
        </p>
      </div>

      <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700">
          Upload Technical Drawings and Figures
        </label>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <Upload className="mx-auto h-12 w-12 text-gray-400" />
          <div className="mt-4">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
            >
              Upload Drawings/Figures
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.dwg"
              onChange={handleImageUpload}
              className="hidden"
            />
          </div>
          <p className="mt-2 text-sm text-gray-500">
            Supports: JPG, PNG, PDF, DWG files
          </p>
        </div>
      </div>

      {uploadedImages.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-medium text-gray-700">Uploaded Files ({uploadedImages.length}):</h3>
          <div className="space-y-2">
            {uploadedImages.map((image) => (
              <div key={image.id} className="flex items-center p-2 bg-gray-50 rounded">
                <FileText className="h-4 w-4 text-gray-400 mr-2" />
                <span className="text-sm text-gray-600">{image.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {uploadedImages.length < 5 && (
        <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
          <p className="text-sm text-orange-800">
            <strong>Recommendation:</strong> You currently have {uploadedImages.length} drawings. 
            Are there additional aspects or components of your invention you can illustrate? 
            More drawings provide stronger support for your application.
          </p>
        </div>
      )}

      <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700">
          For each drawing or figure, provide figure numbers and descriptions
        </label>
        <textarea
          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows="8"
          placeholder="Example:
FIG. 1 - Perspective view of the invention showing the main housing and control interface
FIG. 2 - Cross-sectional side view illustrating internal components
FIG. 3 - Exploded view showing assembly of major parts
..."
          value={responses.drawingDescriptions || ''}
          onChange={(e) => updateResponse('drawingDescriptions', e.target.value)}
        />
      </div>
    </div>
  );

  const renderDetailedSection = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-800">Section 4: Detailed Description of the Invention</h2>
      
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <p className="text-sm text-blue-800">
          <strong>Critical:</strong> Provide disclosure sufficient for a person skilled in the art to make and use the invention.
        </p>
      </div>

      <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700">
          Detailed Description - For each major component, part, module, or process step:
        </label>
        <textarea
          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows="12"
          placeholder="For each component/step, provide:
• Name and description
• Structure and function in technical detail
• Construction, materials, dimensions, specifications
• Operation and interaction with other parts
• Possible alternatives or variations

Example:
The primary housing (10) comprises a rectangular aluminum enclosure measuring approximately 150mm x 100mm x 50mm. The housing includes a front panel (12) with integrated display (14) and control buttons (16, 18, 20)..."
          value={responses.detailedDescription || ''}
          onChange={(e) => updateResponse('detailedDescription', e.target.value)}
        />
      </div>

      <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700">
          Component Details and Interactions
        </label>
        <textarea
          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows="8"
          placeholder="Describe how components interact, operate together, and any special relationships..."
          value={responses.componentDetails || ''}
          onChange={(e) => updateResponse('componentDetails', e.target.value)}
        />
      </div>

      <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700">
          Alternative Embodiments and Variations
        </label>
        <textarea
          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows="6"
          placeholder="Describe alternative designs, variations, and different ways to implement your invention..."
          value={responses.alternatives || ''}
          onChange={(e) => updateResponse('alternatives', e.target.value)}
        />
      </div>

      <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700">
          Manufacturing, Assembly, and Usage Notes (Optional)
        </label>
        <textarea
          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows="6"
          placeholder="Describe special manufacturing processes, assembly instructions, or usage notes..."
          value={responses.manufacturing || ''}
          onChange={(e) => updateResponse('manufacturing', e.target.value)}
        />
      </div>
    </div>
  );

  const renderReviewSection = () => {
    const wordCount = estimateWordCount();
    const isAdequateLength = wordCount >= 8000;
    const hasEnoughDrawings = uploadedImages.length >= 5;

    return (
      <div className="space-y-6">
        <h2 className="text-xl font-bold text-gray-800">Section 5: Review & Page Count Check</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <h3 className="font-semibold text-gray-700 mb-2">Word Count Analysis</h3>
            <p className="text-2xl font-bold text-blue-600">{wordCount.toLocaleString()} words</p>
            <p className="text-sm text-gray-600">Target: 8,000-10,000 words</p>
            {!isAdequateLength && (
              <p className="text-sm text-orange-600 mt-2">
                Consider adding more detail to strengthen your application
              </p>
            )}
          </div>
          
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <h3 className="font-semibold text-gray-700 mb-2">Drawings Count</h3>
            <p className="text-2xl font-bold text-green-600">{uploadedImages.length} figures</p>
            <p className="text-sm text-gray-600">Recommended: 5+ figures</p>
            {!hasEnoughDrawings && (
              <p className="text-sm text-orange-600 mt-2">
                More drawings provide stronger support
              </p>
            )}
          </div>
        </div>

        {(!isAdequateLength || !hasEnoughDrawings) && (
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <h3 className="font-semibold text-yellow-800 mb-2">Strengthen Your Application</h3>
            <p className="text-sm text-yellow-700 mb-3">
              To strengthen your application, please provide:
            </p>
            <ul className="list-disc list-inside text-sm text-yellow-700 space-y-1">
              {!isAdequateLength && <li>More detailed descriptions and technical specifications</li>}
              {!hasEnoughDrawings && <li>Additional drawings showing different views and embodiments</li>}
              <li>Additional embodiments and variations</li>
              <li>Operation examples and experimental data</li>
              <li>More specific technical details</li>
            </ul>
          </div>
        )}

        <div className="space-y-4">
          <button
            onClick={generateDocument}
            className="w-full bg-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700 flex items-center justify-center"
          >
            <Download className="w-5 h-5 mr-2" />
            Download Patent Application Document
          </button>
        </div>

        <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
          <h3 className="font-semibold text-blue-800 mb-3">Next Steps for Filing:</h3>
          <ol className="list-decimal list-inside text-sm text-blue-700 space-y-2">
            <li>
              <strong>Complete the USPTO Form SB/16:</strong> Download and fill out the Provisional Patent Application Cover Sheet from the USPTO website
            </li>
            <li>
              <strong>Prepare your submission:</strong> Attach both the coversheet (Form SB/16) and your application document
            </li>
            <li>
              <strong>Submit your application:</strong> Send through USPTO's Patent Application Filing System (EFS-Web) or by physical mail to the address on the form
            </li>
            <li>
              <strong>Pay the required fees:</strong> Check current USPTO fee schedule for provisional applications
            </li>
          </ol>
          <div className="mt-4 p-3 bg-white rounded border-l-4 border-blue-500">
            <p className="text-xs text-gray-600">
              <strong>Important:</strong> This tool helps prepare your content but does not constitute legal advice. 
              Consider consulting with a patent attorney for complex inventions.
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderCurrentSection = () => {
    switch (currentSection) {
      case 0: return renderIntroSection();
      case 1: return renderBackgroundSection();
      case 2: return renderSummarySection();
      case 3: return renderDrawingsSection();
      case 4: return renderDetailedSection();
      case 5: return renderReviewSection();
      default: return renderIntroSection();
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-gray-50 min-h-screen">
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          {sections.map((section, index) => (
            <div key={section.id} className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                index <= currentSection 
                  ? 'bg-blue-500 border-blue-500 text-white' 
                  : 'bg-white border-gray-300 text-gray-400'
              }`}>
                {index < currentSection ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <span className="text-sm font-semibold">{index + 1}</span>
                )}
              </div>
              {index < sections.length - 1 && (
                <div className={`w-16 h-1 ml-2 ${
                  index < currentSection ? 'bg-blue-500' : 'bg-gray-300'
                }`} />
              )}
            </div>
          ))}
        </div>
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 flex items-center justify-center">
            {sections[currentSection].icon}
            <span className="ml-2">{sections[currentSection].title}</span>
          </h1>
        </div>
      </div>

      {/* Current Section Content */}
      <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
        {renderCurrentSection()}
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
          disabled={currentSection === 0}
          className="flex items-center px-6 py-3 bg-gray-500 text-white rounded-lg font-semibold hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="w-5 h-5 mr-2" />
          Previous
        </button>
        
        {currentSection < sections.length - 1 && (
          <button
            onClick={() => setCurrentSection(Math.min(sections.length - 1, currentSection + 1))}
            className="flex items-center px-6 py-3 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600"
          >
            Next
            <ChevronRight className="w-5 h-5 ml-2" />
          </button>
        )}
      </div>
    </div>
  );
};

export default ProvisionalPatentChatbot;